import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertCircle, ChevronDown, ChevronUp, Plus } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import {
  ASSESSMENT_CHECKPOINTS,
  DISCIPLINE_LABELS,
  SKILL_LEVEL_LABELS,
  getCheckpoints,
  type Discipline,
  type SkillLevel,
} from "@/config/assessmentCheckpoints";

// ─── Types ──────────────────────────────────────────────────────────────────

export interface ScoreEntry {
  checkpointKey: string;
  score: number;
  notes: string | null;
}

export interface Assessment {
  id: number;
  discipline: Discipline;
  skillLevel: SkillLevel;
  status: "intake" | "progress_check" | "milestone";
  assessedAt: string;
  notes: string | null;
  scores: ScoreEntry[];
}

interface Props {
  kidId: number;
}

const STATUS_LABELS: Record<Assessment["status"], string> = {
  intake: "Intake",
  progress_check: "Progress Check",
  milestone: "Milestone",
};

// ─── Score badge color ──────────────────────────────────────────────────────

function scoreBadgeClass(score: number) {
  if (score <= 2) return "bg-red-100 text-red-800 border-red-200 border";
  if (score === 3) return "bg-amber-100 text-amber-800 border-amber-200 border";
  return "bg-green-100 text-green-800 border-green-200 border";
}

function avgScore(scores: ScoreEntry[]): number | null {
  if (scores.length === 0) return null;
  return scores.reduce((sum, s) => sum + s.score, 0) / scores.length;
}

// ─── Development Priorities ─────────────────────────────────────────────────

// Exported for reuse — ParentKidDetail's read-only Progress tab shows the
// same lowest-3-checkpoints view (PR-PARENT-PROGRESS).
export function DevelopmentPriorities({ assessment }: { assessment: Assessment }) {
  const lowest = [...assessment.scores].sort((a, b) => a.score - b.score).slice(0, 3);
  const checkpoints = getCheckpoints(assessment.discipline, assessment.skillLevel);

  return (
    <div className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm">
      <h2 className="text-sm font-semibold text-gray-700 mb-1">Development Priorities</h2>
      <p className="text-xs text-gray-400 mb-4">
        Lowest-scoring checkpoints from the most recent assessment ({DISCIPLINE_LABELS[assessment.discipline]} — {SKILL_LEVEL_LABELS[assessment.skillLevel]})
      </p>
      <div className="space-y-2">
        {lowest.map((s) => {
          const label = checkpoints.find((c) => c.key === s.checkpointKey)?.label ?? s.checkpointKey;
          return (
            <div
              key={s.checkpointKey}
              className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2.5"
            >
              <span className="text-sm font-medium text-gray-800">{label}</span>
              <div className="flex items-center gap-2">
                <Badge className={`text-xs ${scoreBadgeClass(s.score)}`}>{s.score} / 5</Badge>
                {s.score <= 3 && (
                  <Badge className="text-xs bg-amber-50 text-amber-700 border-amber-200 border">
                    Focus area
                  </Badge>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Assessment history ─────────────────────────────────────────────────────

function AssessmentHistoryItem({ assessment }: { assessment: Assessment }) {
  const [expanded, setExpanded] = useState(false);
  const avg = avgScore(assessment.scores);
  const checkpoints = getCheckpoints(assessment.discipline, assessment.skillLevel);

  return (
    <div className="bg-white border border-gray-100 rounded-xl shadow-sm overflow-hidden">
      <button
        type="button"
        className="w-full flex items-center justify-between px-4 py-3 text-left"
        onClick={() => setExpanded((e) => !e)}
        data-testid={`assessment-history-toggle-${assessment.id}`}
      >
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-gray-800">
            {new Date(assessment.assessedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
          </span>
          <Badge variant="secondary" className="text-xs">{DISCIPLINE_LABELS[assessment.discipline]}</Badge>
          <Badge variant="outline" className="text-xs">{SKILL_LEVEL_LABELS[assessment.skillLevel]}</Badge>
          <Badge variant="outline" className="text-xs text-gray-500">{STATUS_LABELS[assessment.status]}</Badge>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm font-bold text-gray-700">
            {avg !== null ? avg.toFixed(1) : "—"}<span className="text-xs font-normal text-gray-400">/5</span>
          </span>
          {expanded ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
        </div>
      </button>

      {expanded && (
        <div className="border-t border-gray-100 px-4 py-3">
          <table className="w-full text-sm">
            <tbody className="divide-y divide-gray-50">
              {assessment.scores.map((s) => (
                <tr key={s.checkpointKey}>
                  <td className="py-1.5 text-gray-700">
                    {checkpoints.find((c) => c.key === s.checkpointKey)?.label ?? s.checkpointKey}
                  </td>
                  <td className="py-1.5 text-right font-mono text-gray-800">{s.score}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {assessment.notes && (
            <p className="text-xs text-gray-500 mt-3 italic">{assessment.notes}</p>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Assessment form dialog (3 steps) ──────────────────────────────────────

const DISCIPLINES: Discipline[] = ["batting", "spin_bowling", "pace_bowling"];
const SKILL_LEVELS: SkillLevel[] = ["beginner", "intermediate", "advanced"];

interface FormDialogProps {
  kidId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function AssessmentFormDialog({ kidId, open, onOpenChange }: FormDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [discipline, setDiscipline] = useState<Discipline>("batting");
  const [skillLevel, setSkillLevel] = useState<SkillLevel>("intermediate");
  const [status, setStatus] = useState<Assessment["status"]>("intake");
  const [scores, setScores] = useState<Record<string, number>>({});
  const [checkpointNotes, setCheckpointNotes] = useState<Record<string, string>>({});
  const [expandedNotes, setExpandedNotes] = useState<Set<string>>(new Set());
  const [overallNotes, setOverallNotes] = useState("");

  const checkpoints = getCheckpoints(discipline, skillLevel);
  const allScored = checkpoints.every((c) => scores[c.key] != null);

  function reset() {
    setStep(1);
    setDiscipline("batting");
    setSkillLevel("intermediate");
    setStatus("intake");
    setScores({});
    setCheckpointNotes({});
    setExpandedNotes(new Set());
    setOverallNotes("");
  }

  function close() {
    onOpenChange(false);
    reset();
  }

  const submitMutation = useMutation({
    mutationFn: async () => {
      const res: any = await apiRequest("POST", `/api/players/${kidId}/assessments`, {
        discipline,
        skillLevel,
        status,
        notes: overallNotes.trim() || undefined,
        scores: checkpoints.map((c) => ({
          checkpointKey: c.key,
          score: scores[c.key],
          notes: checkpointNotes[c.key]?.trim() || undefined,
        })),
      });
      return res.data;
    },
    onSuccess: () => {
      toast({ title: "Assessment recorded", description: "The assessment has been saved." });
      queryClient.invalidateQueries({ queryKey: ["player-assessments", kidId] });
      close();
    },
    onError: (error: Error) => {
      toast({ title: "Failed to save assessment", description: error.message, variant: "destructive" });
    },
  });

  const lowestThree = [...checkpoints]
    .map((c) => ({ ...c, score: scores[c.key] }))
    .sort((a, b) => (a.score ?? 0) - (b.score ?? 0))
    .slice(0, 3);

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(o) : close())}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="assessment-form-dialog">
        <DialogHeader>
          <DialogTitle>
            {step === 1 && "New Assessment — Setup"}
            {step === 2 && `${DISCIPLINE_LABELS[discipline]} — ${SKILL_LEVEL_LABELS[skillLevel]} Assessment`}
            {step === 3 && "Review & Submit"}
          </DialogTitle>
        </DialogHeader>

        {/* Step 1 — Setup */}
        {step === 1 && (
          <div className="space-y-5 py-2">
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Discipline</p>
              <div className="grid grid-cols-3 gap-3">
                {DISCIPLINES.map((d) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => setDiscipline(d)}
                    data-testid={`discipline-card-${d}`}
                    className={`rounded-xl border-2 p-4 text-center transition-colors ${
                      discipline === d
                        ? "border-blue-500 bg-blue-50 text-blue-700"
                        : "border-gray-200 text-gray-600 hover:border-gray-300"
                    }`}
                  >
                    <span className="text-sm font-semibold">{DISCIPLINE_LABELS[d]}</span>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Skill Level</p>
              <div className="flex gap-2">
                {SKILL_LEVELS.map((lvl) => (
                  <button
                    key={lvl}
                    type="button"
                    onClick={() => setSkillLevel(lvl)}
                    data-testid={`skill-level-pill-${lvl}`}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                      skillLevel === lvl
                        ? "bg-blue-600 text-white border-blue-600"
                        : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    {SKILL_LEVEL_LABELS[lvl]}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Assessment Type</p>
              <div className="flex gap-2">
                {(Object.keys(STATUS_LABELS) as Assessment["status"][]).map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setStatus(s)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                      status === s
                        ? "bg-gray-800 text-white border-gray-800"
                        : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    {STATUS_LABELS[s]}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Step 2 — Checkpoints */}
        {step === 2 && (
          <div className="space-y-3 py-2">
            {checkpoints.map((c) => (
              <div key={c.key} className="border border-gray-100 rounded-lg p-3">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div>
                    <p className="text-sm font-medium text-gray-800">{c.label}</p>
                    <p className="text-xs text-gray-400">{c.description}</p>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => setScores((s) => ({ ...s, [c.key]: n }))}
                        data-testid={`score-btn-${c.key}-${n}`}
                        className={`h-7 w-7 rounded-full text-xs font-semibold border transition-colors ${
                          scores[c.key] === n
                            ? "bg-blue-600 text-white border-blue-600"
                            : "bg-white text-gray-500 border-gray-200 hover:border-gray-300"
                        }`}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                </div>
                {expandedNotes.has(c.key) ? (
                  <Textarea
                    placeholder="Optional note..."
                    className="text-sm"
                    rows={2}
                    value={checkpointNotes[c.key] ?? ""}
                    onChange={(e) => setCheckpointNotes((n) => ({ ...n, [c.key]: e.target.value }))}
                  />
                ) : (
                  <button
                    type="button"
                    className="text-xs text-blue-600 hover:underline"
                    onClick={() => setExpandedNotes((s) => new Set(s).add(c.key))}
                  >
                    + add note
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Step 3 — Summary */}
        {step === 3 && (
          <div className="space-y-4 py-2">
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Overall Notes (optional)</p>
              <Textarea
                placeholder="Session summary, observations..."
                rows={3}
                value={overallNotes}
                onChange={(e) => setOverallNotes(e.target.value)}
              />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Will become Development Priorities:</p>
              <div className="space-y-1.5">
                {lowestThree.map((c) => (
                  <div key={c.key} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2 text-sm">
                    <span className="text-gray-700">{c.label}</span>
                    <Badge className={`text-xs ${scoreBadgeClass(c.score ?? 0)}`}>{c.score} / 5</Badge>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="flex items-center justify-between sm:justify-between">
          {step > 1 ? (
            <Button variant="ghost" onClick={() => setStep((s) => (s - 1) as 1 | 2 | 3)}>← Back</Button>
          ) : (
            <span />
          )}
          {step < 3 ? (
            <Button
              onClick={() => setStep((s) => (s + 1) as 1 | 2 | 3)}
              disabled={step === 2 && !allScored}
              data-testid="assessment-next-btn"
            >
              Next →
            </Button>
          ) : (
            <Button
              onClick={() => submitMutation.mutate()}
              disabled={submitMutation.isPending}
              data-testid="assessment-submit-btn"
            >
              {submitMutation.isPending ? "Submitting..." : "Submit Assessment"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main tab ───────────────────────────────────────────────────────────────

export function KidAssessmentTab({ kidId }: Props) {
  const [dialogOpen, setDialogOpen] = useState(false);

  const { data, isLoading, isError, refetch } = useQuery<{ assessments: Assessment[] }>({
    queryKey: ["player-assessments", kidId],
    queryFn: async () => {
      const res: any = await apiRequest("GET", `/api/players/${kidId}/assessments`);
      return res.data;
    },
    enabled: !!kidId,
    retry: false,
  });

  if (isLoading) {
    return (
      <div className="space-y-4 py-4">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-32 w-full rounded-xl" />
        <Skeleton className="h-20 w-full rounded-xl" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center gap-3">
        <AlertCircle className="h-8 w-8 text-red-400" />
        <p className="text-sm text-gray-500">Could not load assessments.</p>
        <Button variant="outline" size="sm" onClick={() => refetch()}>Retry</Button>
      </div>
    );
  }

  const assessments = data?.assessments ?? [];

  if (assessments.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center text-gray-400" data-testid="assessment-empty-state">
        <p className="font-medium text-gray-500">No assessments recorded</p>
        <p className="text-sm mt-1 mb-4">Start an intake assessment to track development priorities.</p>
        <Button onClick={() => setDialogOpen(true)} data-testid="start-intake-assessment-btn">
          <Plus className="h-4 w-4 mr-1.5" />
          Start Intake Assessment
        </Button>
        <AssessmentFormDialog kidId={kidId} open={dialogOpen} onOpenChange={setDialogOpen} />
      </div>
    );
  }

  const mostRecent = assessments[0];

  return (
    <div className="space-y-5 py-2" data-testid="tab-content-assessment-loaded">
      <div className="flex items-center justify-end">
        <Button size="sm" onClick={() => setDialogOpen(true)} data-testid="new-assessment-btn">
          <Plus className="h-4 w-4 mr-1.5" />
          New Assessment
        </Button>
      </div>

      <DevelopmentPriorities assessment={mostRecent} />

      <div>
        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Assessment History</h2>
        <div className="space-y-2">
          {assessments.map((a) => (
            <AssessmentHistoryItem key={a.id} assessment={a} />
          ))}
        </div>
      </div>

      <AssessmentFormDialog kidId={kidId} open={dialogOpen} onOpenChange={setDialogOpen} />
    </div>
  );
}
